import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Skeleton,
  LinearProgress,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  People as PeopleIcon,
  DirectionsCar as CarIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteLogoIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { clientService } from '../services/api';
import type { Client } from '../types';

const AdminDashboard: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showManageUsersDialog, setShowManageUsersDialog] = useState(false);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [clientUsers, setClientUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<Partial<Client>>({
    name: '',
    civilite: '',
    date_naissance: '',
    lieu_naissance: '',
    email: '',
    phone: '',
    address: '',
    accept_sms_email: false,
    subscription_plan: 'basic',
    max_vehicles: 50,
    max_users: 5,
  });
  
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role_name: 'client' as 'client' | 'field_agent',
  });
  
  const [editUserFormData, setEditUserFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setError(null);
      if (!bootstrapped) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      const response = await clientService.getAll();
      setClients(response.data.data);
    } catch (err: any) {
      console.error('Error loading clients:', err);
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setBootstrapped(true);
    }
  };

  const handleCreateClient = async () => {
    try {
      await clientService.create(formData);
      setShowCreateDialog(false);
      setFormData({
        name: '',
        civilite: '',
        date_naissance: '',
        lieu_naissance: '',
        email: '',
        phone: '',
        address: '',
        accept_sms_email: false,
        subscription_plan: 'basic',
        max_vehicles: 50,
        max_users: 5,
      });
      loadClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create client');
    }
  };

  const handleEditClient = async () => {
    if (!selectedClient) return;
    
    try {
      // Update client data
      await clientService.update(selectedClient.id, formData);
      
      // Upload logo if a file is selected
      if (logoFile) {
        await clientService.uploadLogo(selectedClient.id, logoFile);
      }
      
      setShowEditDialog(false);
      setSelectedClient(null);
      setLogoFile(null);
      loadClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update client');
    }
  };

  const handleDeleteClient = async (client: Client) => {
    if (window.confirm(`Are you sure you want to delete ${client.name}? This action cannot be undone.`)) {
      try {
        console.log('Deleting client:', client.id);
        await clientService.delete(client.id);
        console.log('Client deleted successfully');
        loadClients();
      } catch (err: any) {
        console.error('Error deleting client:', err);
        const errorMessage = err.response?.data?.error || err.response?.data?.message || 'Failed to delete client';
        setError(errorMessage);
      }
    }
  };

  const openEditDialog = (client: Client) => {
    setSelectedClient(client);
    setFormData({
      name: client.name,
      civilite: client.civilite,
      date_naissance: client.date_naissance,
      lieu_naissance: client.lieu_naissance,
      email: client.email,
      phone: client.phone,
      address: client.address,
      accept_sms_email: client.accept_sms_email ?? false,
      subscription_plan: client.subscription_plan,
      subscription_status: client.subscription_status,
      max_vehicles: client.max_vehicles,
      max_users: client.max_users,
      is_active: client.is_active,
    });
    setLogoFile(null); // Clear any previously selected file
    setShowEditDialog(true);
  };

  const openCreateUserDialog = (client: Client) => {
    setSelectedClient(client);
    setUserFormData({
      name: '',
      email: '',
      password: '',
      phone: client.phone || '',
      role_name: 'client',
    });
    setShowCreateUserDialog(true);
  };

  const handleCreateUser = async () => {
    if (!selectedClient) return;
    
    try {
      await clientService.createUser(selectedClient.id, userFormData);
      setShowCreateUserDialog(false);
      setUserFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        role_name: 'client',
      });
      loadClients(); // Reload to get updated user count
      if (showManageUsersDialog) {
        loadClientUsers(); // Reload users list if manage dialog is open
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const openManageUsersDialog = async (client: Client) => {
    setSelectedClient(client);
    setShowManageUsersDialog(true);
    await loadClientUsers(client);
  };

  const loadClientUsers = async (client?: Client) => {
    const targetClient = client || selectedClient;
    if (!targetClient) return;
    
    try {
      setLoadingUsers(true);
      const response = await clientService.getUsers(targetClient.id);
      setClientUsers(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async (user: any) => {
    if (!selectedClient) return;
    
    if (window.confirm(`Are you sure you want to delete user ${user.name}?`)) {
      try {
        await clientService.deleteUser(selectedClient.id, user.id);
        await loadClientUsers();
        loadClients(); // Reload to get updated user count
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  const openEditUserDialog = (user: any) => {
    setSelectedUser(user);
    setEditUserFormData({
      name: user.name || '',
      email: user.email || '',
      password: '', // Don't pre-fill password for security
      phone: user.phone || '',
    });
    setShowEditUserDialog(true);
  };

  const handleEditUser = async () => {
    if (!selectedClient || !selectedUser) return;
    
    try {
      // Only send fields that have been changed
      const updateData: any = {};
      if (editUserFormData.name !== selectedUser.name) {
        updateData.name = editUserFormData.name;
      }
      if (editUserFormData.email !== selectedUser.email) {
        updateData.email = editUserFormData.email;
      }
      if (editUserFormData.phone !== selectedUser.phone) {
        updateData.phone = editUserFormData.phone;
      }
      if (editUserFormData.password.trim() !== '') {
        updateData.password = editUserFormData.password;
      }

      await clientService.updateUser(selectedClient.id, selectedUser.id, updateData);
      setShowEditUserDialog(false);
      setEditUserFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
      });
      setSelectedUser(null);
      await loadClientUsers();
      loadClients(); // Reload to get updated data
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };



  const handleLogoDelete = async (client: Client) => {
    if (window.confirm(`Are you sure you want to delete the logo for ${client.name}?`)) {
      try {
        await clientService.deleteLogo(client.id);
        loadClients(); // Reload to get updated data
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete logo');
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      setLogoFile(file);
      setError(null);
    }
  };

  const getSubscriptionColor = (plan: string) => {
    switch (plan) {
      case 'basic': return 'default';
      case 'premium': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading && !bootstrapped) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        sx={{ p: 3, maxWidth: '100%', overflowX: 'hidden', minHeight: '60vh' }}
      >
        <Typography
          component="span"
          sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}
        >
          Chargement du tableau d&apos;administration…
        </Typography>
        <Skeleton variant="text" width={320} height={44} sx={{ mb: 3 }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, md: 3 }}>
              <Card>
                <CardContent>
                  <Skeleton variant="text" width="55%" />
                  <Skeleton variant="text" width="35%" height={40} sx={{ my: 1 }} />
                  <Skeleton variant="text" width="70%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Skeleton variant="text" width={120} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[0, 1, 2].map((j) => (
              <Grid key={j} size={{ xs: 12, md: 6, lg: 4 }}>
                <Skeleton variant="rounded" height={160} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Box>
    );
  }

  const totalVehicleCount = clients.reduce((sum, client) => sum + (client.vehicles?.length || 0), 0);

  return (
    <Box sx={{ p: 3, maxWidth: '100%', overflowX: 'hidden' }} aria-busy={refreshing}>
      {refreshing && (
        <LinearProgress
          sx={{ position: 'sticky', top: 0, zIndex: 10, mb: 2, borderRadius: 1 }}
          aria-label="Actualisation des clients"
        />
      )}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" component="h1">
          Admin Dashboard
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Actualiser la liste">
            <span>
              <IconButton
                onClick={() => loadClients()}
                disabled={refreshing}
                aria-label="Actualiser la liste des clients"
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowCreateDialog(true)}
          >
            Create Client
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <BusinessIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h4">
                    {clients.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Clients
                  </Typography>
                  <Typography variant="h4">
                    {clients.filter(c => c.subscription_status === 'active').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <CarIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Vehicles
                  </Typography>
                  <Typography variant="h4">
                    {totalVehicleCount}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Somme des véhicules renvoyés avec chaque client (relation chargée par l&apos;API).
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Users
                  </Typography>
                  <Typography variant="h4">
                    {clients.reduce((sum, client) => sum + (client.users?.length || 0), 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Somme des utilisateurs renvoyés avec chaque client.
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Clients List */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Clients
        </Typography>
        <Grid container spacing={2}>
          {clients.map((client) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={client.id}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {client.logo_path ? (
                        <Avatar
                          src={client.logo_url}
                          sx={{ width: 40, height: 40 }}
                          alt={`${client.name} logo`}
                        />
                      ) : (
                        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                          <BusinessIcon />
                        </Avatar>
                      )}
                      <Typography variant="h6" component="div">
                        {client.name}
                      </Typography>
                    </Box>
                    <Box>
                      <Chip
                        label={client.subscription_plan}
                        color={getSubscriptionColor(client.subscription_plan)}
                        size="small"
                        sx={{ mr: 1 }}
                      />
                      <Chip
                        label={client.subscription_status}
                        color={getStatusColor(client.subscription_status)}
                        size="small"
                      />
                    </Box>
                  </Box>
                  
                  <Typography color="textSecondary" gutterBottom>
                    {client.email}
                  </Typography>
                  
                  {client.phone && (
                    <Typography color="textSecondary" gutterBottom>
                      {client.phone}
                    </Typography>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2" color="textSecondary">
                      Vehicles: {client.vehicles?.length || 0}/{client.max_vehicles}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Users: {client.users?.length || 0}/{client.max_users}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="textSecondary">
                      Parking Spots: {client.parking_spots?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Parking Plans: {client.parking_layouts?.length || 0}
                    </Typography>
                  </Box>
                </CardContent>
                
                <CardActions>
                  <IconButton size="small" onClick={() => openCreateUserDialog(client)} title="Add User">
                    <PersonAddIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => openManageUsersDialog(client)} title="Manage Users">
                    <PeopleIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => openEditDialog(client)} title="Edit Client">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClient(client)} title="Delete Client">
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Create Client Dialog */}
      <Dialog open={showCreateDialog} onClose={() => setShowCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Client Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Civilité</InputLabel>
                <Select
                  value={formData.civilite ?? ''}
                  label="Civilité"
                  onChange={(e) => setFormData({ ...formData, civilite: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="Mme">Mme</MenuItem>
                  <MenuItem value="Mlle">Mlle</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Date de naissance"
                value={formData.date_naissance ?? ''}
                onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Lieu de naissance"
                value={formData.lieu_naissance ?? ''}
                onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={formData.subscription_plan}
                  onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value as any })}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.accept_sms_email ?? false}
                    onChange={(e) => setFormData({ ...formData, accept_sms_email: e.target.checked })}
                  />
                }
                label="Accepte SMS / email"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Max Vehicles"
                type="number"
                value={formData.max_vehicles}
                onChange={(e) => setFormData({ ...formData, max_vehicles: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Max Users"
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateClient} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Client Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Civilité</InputLabel>
                <Select
                  value={formData.civilite ?? ''}
                  label="Civilité"
                  onChange={(e) => setFormData({ ...formData, civilite: e.target.value })}
                >
                  <MenuItem value="">—</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="Mme">Mme</MenuItem>
                  <MenuItem value="Mlle">Mlle</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                type="date"
                label="Date de naissance"
                value={formData.date_naissance ?? ''}
                onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <TextField
                fullWidth
                label="Lieu de naissance"
                value={formData.lieu_naissance ?? ''}
                onChange={(e) => setFormData({ ...formData, lieu_naissance: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={formData.subscription_plan}
                  onChange={(e) => setFormData({ ...formData, subscription_plan: e.target.value as any })}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.subscription_status}
                  onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value as any })}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Max Vehicles"
                type="number"
                value={formData.max_vehicles}
                onChange={(e) => setFormData({ ...formData, max_vehicles: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Max Users"
                type="number"
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.accept_sms_email ?? false}
                    onChange={(e) => setFormData({ ...formData, accept_sms_email: e.target.checked })}
                  />
                }
                label="Accepte SMS / email"
              />
            </Grid>
            
            {/* Logo Upload Section */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Logo
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                {logoFile ? (
                  <Avatar
                    src={URL.createObjectURL(logoFile)}
                    sx={{ width: 60, height: 60 }}
                    alt="Selected logo preview"
                  />
                ) : selectedClient?.logo_path ? (
                  <Avatar
                    src={selectedClient.logo_url}
                    sx={{ width: 60, height: 60 }}
                    alt={`${selectedClient.name} logo`}
                  />
                ) : (
                  <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                    <BusinessIcon />
                  </Avatar>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {logoFile ? 'New logo selected' : selectedClient?.logo_path ? 'Current logo' : 'No logo uploaded'}
                  </Typography>
                  {selectedClient?.logo_path && !logoFile && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => selectedClient && handleLogoDelete(selectedClient)}
                      startIcon={<DeleteLogoIcon />}
                    >
                      Remove Logo
                    </Button>
                  )}
                </Box>
              </Box>
              
              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                  >
                    {logoFile ? `Change Logo (${logoFile.name})` : 'Select New Logo'}
                  </Button>
                </label>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditClient} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onClose={() => setShowCreateUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User for {selectedClient?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="User Name"
                value={userFormData.name}
                onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={userFormData.password}
                onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={userFormData.phone}
                onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={userFormData.role_name}
                  label="User Type"
                  onChange={(e) => setUserFormData({ ...userFormData, role_name: e.target.value as 'client' | 'field_agent' })}
                >
                  <MenuItem value="client">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Utilisateur Client</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Accès complet au système pour gérer les véhicules
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                  <MenuItem value="field_agent">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonAddIcon fontSize="small" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Agent Terrain</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Interface mobile pour scanner et gérer les véhicules
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateUserDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Users Dialog */}
      <Dialog open={showManageUsersDialog} onClose={() => setShowManageUsersDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Users for {selectedClient?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              disabled={loadingUsers}
              onClick={() => {
                setShowManageUsersDialog(false);
                openCreateUserDialog(selectedClient!);
              }}
            >
              Add New User
            </Button>
          </Box>
          
          <List>
            {loadingUsers ? (
              <ListItem>
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 2 }}>
                    Loading users...
                  </Typography>
                </Box>
              </ListItem>
            ) : (
              <>
                {clientUsers.map((user) => (
                  <React.Fragment key={user.id}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography>{user.name}</Typography>
                            <Chip 
                              label={user.role?.name === 'field_agent' ? 'Agent Terrain' : 'Client'}
                              size="small"
                              color={user.role?.name === 'field_agent' ? 'secondary' : 'default'}
                              icon={user.role?.name === 'field_agent' ? <PersonAddIcon /> : <BusinessIcon />}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="textSecondary">
                              {user.email}
                            </Typography>
                            {user.phone && (
                              <Typography variant="body2" color="textSecondary">
                                {user.phone}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          size="small" 
                          variant="outlined"
                          onClick={() => openEditUserDialog(user)}
                        >
                          Edit
                        </Button>
                        <Button 
                          size="small" 
                          color="error" 
                          variant="outlined"
                          onClick={() => handleDeleteUser(user)}
                        >
                          Delete
                        </Button>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
                {clientUsers.length === 0 && (
                  <ListItem>
                    <ListItemText 
                      primary="No users found" 
                      secondary="Click 'Add New User' to create the first user for this client"
                    />
                  </ListItem>
                )}
              </>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowManageUsersDialog(false);
            setClientUsers([]); // Clear users list when closing
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onClose={() => setShowEditUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User: {selectedUser?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={editUserFormData.name}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editUserFormData.email}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Phone"
                value={editUserFormData.phone}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, phone: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="New Password (leave blank to keep current)"
                type="password"
                value={editUserFormData.password}
                onChange={(e) => setEditUserFormData({ ...editUserFormData, password: e.target.value })}
                helperText="Enter a new password only if you want to change it"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained">
            Update User
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default AdminDashboard;
